// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

export const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL || '').trim();
export const SUPABASE_PUBLISHABLE_KEY = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '').trim();

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    'Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in your .env.local file.'
  );
}

// Resilient wrapper around supabase-js's Navigator LockManager auth lock.
// The default lock throws an unhandled rejection when contention exceeds 10s
// (e.g. a tab in the OAuth callback flow holding the lock while another tab
// boots). For a single-tab SPA, falling back to running unlocked is safer
// than rejecting — duplicate token refreshes are idempotent server-side,
// but a rejected boot blocks the user. Logs to console so the rare event is
// still observable via Sentry breadcrumbs once that's live.
const safeAuthLock = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return fn();
  }
  try {
    return (await navigator.locks.request(
      name,
      { mode: 'exclusive', signal: AbortSignal.timeout(acquireTimeout) },
      async () => fn(),
    )) as R;
  } catch (err) {
    // AbortError (timeout) or NotSupportedError — degrade gracefully rather
    // than surfacing a 10-second auth boot failure to the user.
    console.warn(
      `[supabase] auth lock "${name}" unavailable (${(err as Error)?.name ?? 'unknown'}); proceeding unlocked`,
    );
    return fn();
  }
};

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    lock: safeAuthLock,
  }
});

const AUTH_KEY = `sb-${new URL(SUPABASE_URL).hostname.split('.')[0]}-auth-token`;

async function revokeStored(current: () => boolean): Promise<void> {
  try {
    const session = JSON.parse(localStorage.getItem(AUTH_KEY) ?? 'null') as {
      access_token?: string; refresh_token?: string; expires_at?: number;
    } | null;
    let token = session?.access_token;
    if (session?.refresh_token && (session.expires_at ?? 0) * 1000 < Date.now() + 30_000) {
      // Never refresh through the SDK here: its late response can delete or
      // overwrite a new login even after the revocation deadline has passed.
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: { apikey: SUPABASE_PUBLISHABLE_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: session.refresh_token }),
      });
      if (!response.ok) console.warn('[supabase] refresh before revoke failed', response.status);
      const refreshed = response.ok ? await response.json() as { access_token?: unknown } : null;
      token = typeof refreshed?.access_token === 'string' ? refreshed.access_token : undefined;
    }
    if (token && current()) {
      // This endpoint revokes only the captured session and never writes storage.
      const { error } = await supabase.auth.admin.signOut(token, 'local');
      if (error) console.warn('[supabase] server session revocation failed', error);
    }
  } catch (error) {
    console.warn('[supabase] could not revoke stored session; continuing local cleanup', error);
  }
}

/**
 * Revoke the session before clearing local credentials, with a 1.5s local
 * cleanup fallback when the auth SDK or network is unavailable.
 *
 * Why: when a refresh_token in localStorage is invalidated (token
 * rotation, server-side revocation, user signed in on another device),
 * supabase-js silently retries the refresh on every operation. The
 * `navigator.locks` lock that serializes auth ops gets held by the
 * hung refresh attempt, so subsequent calls — including signOut and
 * signInWithPassword — wait forever on the lock.
 *
 * Symptoms when this happens:
 *   - Sign-in button shows a spinner that never resolves
 *   - Network panel shows bursts of POST to /auth/v1/token with 4xx/429
 *   - Users report "I had to clear cache and 3p cookies to log in"
 *
 * Local cleanup bypasses a stuck lock after a bounded revocation attempt
 * using captured credentials, without an SDK session read or refresh.
 */
export async function forceClearSupabaseAuth(): Promise<void> {
  // Bound the network wait so a stale session cannot strand the user.
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let awaitingRevocation = true;
  try {
    await Promise.race([
      revokeStored(() => awaitingRevocation),
      new Promise((resolve) => { timeout = setTimeout(resolve, 1500); }),
    ]);
  } finally {
    awaitingRevocation = false;
    if (timeout !== undefined) clearTimeout(timeout);
  }
  // Direct localStorage purge — sb-* keys are stable across supabase-js
  // versions. Includes the chunked token (.0, .1, ...) and PKCE verifier.
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sb-") && (k.endsWith("-auth-token") || k.includes("-auth-token."))) {
        keysToRemove.push(k);
      }
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token-code-verifier")) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch (error) {
    console.warn('[supabase] local credential cleanup failed', error);
  }
  // Match auth-js's channel name and payload so subscribers in other tabs
  // (and this client's own channel) see the completed local logout.
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(AUTH_KEY);
      try {
        channel.postMessage({ event: 'SIGNED_OUT', session: null });
      } finally {
        channel.close();
      }
    } catch (error) {
      console.warn('[supabase] could not broadcast local logout', error);
    }
  }
}
